/* Carusel video UGC / „Vedetele poartă GT” (sections/nubra-ugc-videos).
 * Bandă (QA #1, 1 oct 2026): în carusel rulează UN SINGUR video, prin hls.js forțat, plafonat la lățimea plăcii (~480p pe
 * plăci de 300 px; Chrome desktop are HLS nativ și, lăsat singur, urcă la 1080p). Celelalte plăci n-au sursă
 * (preload="none"); clipul care iese din rol/ecran e oprit și i se eliberează bufferul (src scos + load()).
 * HLS (nativ sau hls.js cu capLevelToPlayerSize) doar în ecranul complet, tot un singur video activ.
 * Două așezări: „strip” (rând) și „smart” (GT: clipul central rulează mut, avansează după N s de redare, bare de progres).
 */
(() => {
  if (window.NubraUgc) return;
  const HLS_TYPE = 'application/vnd.apple.mpegurl';
  const nativeHls = (() => {
    try { return document.createElement('video').canPlayType(HLS_TYPE) !== ''; } catch (e) { return false; }
  })();
  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let hlsPromise = null;

  const ensureHls = (src) => {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (!hlsPromise) {
      hlsPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = () => resolve(window.Hls);
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return hlsPromise;
  };

  const makeVideo = (cls) => {
    const v = document.createElement('video');
    v.className = cls || '';
    v.muted = true; v.setAttribute('muted', '');
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.loop = true; v.preload = 'none';
    v.disableRemotePlayback = true;
    return v;
  };

  const tryPlay = (v) => {
    try { const p = v.play(); if (p) p.catch(() => {}); } catch (e) { /* politica de autoplay */ }
  };

  /* Oprește și eliberează bufferul (inclusiv instanța hls.js). */
  const release = (v) => {
    if (!v) return;
    try { v.pause(); } catch (e) {}
    if (v._hls) { try { v._hls.destroy(); } catch (e) {} v._hls = null; }
    if (v.getAttribute('src') || v._nbu) { v.removeAttribute('src'); try { v.load(); } catch (e) {} }
    v.preload = 'none';
    v._nbu = null;
    v.classList.remove('is-on');
  };

  /* Placa din carusel: HLS prin hls.js ÎNTOTDEAUNA (și unde browserul are HLS nativ, ex. Chrome desktop — altfel urcă la 1080p),
     cu nivelul plafonat la lățimea plăcii (×DPR, max 1.5) → pe plăci de ~300 px rămâne la ~480p. Buffer mic.
     Fără MSE (iOS vechi): HLS nativ; fără HLS: MP4. Liquid expune doar m3u8 + MP4 1080p (randările mici nu apar în video.sources). */
  const attachInline = (v, card, hlsSrc) => {
    if (v._nbu) return v._nbu;
    v.preload = 'auto';
    const hls = card.dataset.hls; const mp4 = card.dataset.mp4;
    if (!hls) { if (mp4) v.src = mp4; v._nbu = Promise.resolve(); return v._nbu; }
    const fallback = () => { if (nativeHls) v.src = hls; else if (mp4) v.src = mp4; };
    const token = ensureHls(hlsSrc).then((H) => {
      if (v._nbu !== token) return; // eliberat între timp
      if (H && H.isSupported()) {
        const need = card.clientWidth * Math.min(window.devicePixelRatio || 1, 1.5) * 1.2;
        const h = new H({ startLevel: 0, capLevelToPlayerSize: false, maxBufferLength: 4, maxMaxBufferLength: 6, maxBufferSize: 600 * 1000, backBufferLength: 0 });
        h.on(H.Events.MANIFEST_PARSED, (_e, d) => {
          let cap = 0;
          (d.levels || []).forEach((l, i) => { if ((l.width || 0) <= need) cap = i; });
          h.autoLevelCapping = cap;
        });
        h.on(H.Events.ERROR, (_e, d) => {
          if (d && d.fatal) { try { h.destroy(); } catch (e) {} v._hls = null; if (v._nbu === token) { fallback(); tryPlay(v); } }
        });
        h.loadSource(hls); h.attachMedia(v); v._hls = h;
      } else fallback();
    }).catch(() => { if (v._nbu === token) fallback(); });
    v._nbu = token;
    return token;
  };

  /* Ecran complet: HLS (Safari/Chrome nativ; altfel hls.js plafonat la mărimea playerului), MP4 ≤720p rezervă. */
  const attachHls = (v, hls, mp4, hlsSrc) => {
    if (v._nbu) return v._nbu;
    v.preload = 'auto';
    if (hls && nativeHls) { v.src = hls; v._nbu = Promise.resolve(); return v._nbu; }
    if (!hls) { if (mp4) v.src = mp4; v._nbu = Promise.resolve(); return v._nbu; }
    v._nbu = ensureHls(hlsSrc).then((H) => {
      if (!v._nbu) return; // eliberat între timp
      if (H && H.isSupported()) {
        const h = new H({ capLevelToPlayerSize: true, maxBufferLength: 8, maxMaxBufferLength: 16 });
        h.loadSource(hls); h.attachMedia(v); v._hls = h;
        h.on(H.Events.ERROR, (_e, d) => {
          if (d && d.fatal) { try { h.destroy(); } catch (e) {} v._hls = null; if (mp4) { v.src = mp4; tryPlay(v); } }
        });
      } else if (mp4) { v.src = mp4; }
    }).catch(() => { if (mp4) v.src = mp4; });
    return v._nbu;
  };

  /* Video-ul inline al unui card (creat la nevoie, fără sursă). */
  const videoOf = (card, onPlaying, onError) => {
    if (!card._v) {
      const v = makeVideo('nbu__vid');
      v.setAttribute('aria-hidden', 'true');
      v.addEventListener('playing', () => { v.classList.add('is-on'); card.classList.add('is-playing'); onPlaying && onPlaying(card); });
      v.addEventListener('error', () => { if (v._nbu) onError && onError(card); });
      card.insertBefore(v, card.querySelector('.nbu__shade'));
      card._v = v;
    }
    return card._v;
  };
  const releaseCard = (card) => { if (card && card._v) { release(card._v); card.classList.remove('is-playing'); } };

  /* ---------- ecran complet (reels, cu sunet) — comun ambelor așezări ---------- */
  const wireReels = (root, cards, hlsSrc, hooks) => {
    const dlg = root.querySelector('[data-nbu-reels]');
    if (!dlg) return { open: () => {}, isOpen: () => false };
    const list = dlg.querySelector('[data-nbu-reels-list]');
    const ctaTpl = dlg.querySelector('[data-nbu-cta-label]');
    const cta = ctaTpl ? ctaTpl.innerHTML.trim() : '';
    const muteBtn = dlg.querySelector('[data-nbu-mute]');
    let items = [];
    let muted = false;
    let active = -1;
    let open = false;

    const syncMute = () => {
      if (!muteBtn) return;
      muteBtn.setAttribute('aria-pressed', String(muted));
      muteBtn.querySelector('[data-on]').hidden = muted;
      muteBtn.querySelector('[data-off]').hidden = !muted;
    };

    const activate = (i) => {
      if (i === active || !items[i]) return;
      active = i;
      items.forEach((x, j) => { if (j !== i) { release(x.v); x.el.classList.remove('is-paused'); } });
      const cur = items[i];
      cur.v.muted = muted;
      attachHls(cur.v, cur.card.dataset.hls, cur.card.dataset.mp4, hlsSrc);
      tryPlay(cur.v);
      cur.v._nbu.then(() => { if (active === i) tryPlay(cur.v); });
    };

    const build = () => {
      if (items.length) return;
      cards.forEach((card, i) => {
        const it = document.createElement('div');
        it.className = 'nbu-reels__item';
        it.dataset.i = String(i);
        const v = makeVideo('');
        v.muted = muted;
        v.setAttribute('aria-label', card.getAttribute('aria-label') || '');
        const poster = card.querySelector('.nbu__poster');
        if (poster) v.poster = poster.currentSrc || poster.src || poster.getAttribute('data-src') || '';
        it.appendChild(v);
        const tap = document.createElement('span');
        tap.className = 'nbu-reels__tap';
        tap.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>';
        it.appendChild(tap);
        if (card.dataset.name) {
          const who = document.createElement('span');
          who.className = 'nbu-reels__who';
          who.textContent = card.dataset.name;
          it.appendChild(who);
        }
        if (card.dataset.pUrl) {
          const a = document.createElement('a');
          a.className = 'nbu-reels__prod';
          a.href = card.dataset.pUrl;
          const img = card.dataset.pImg ? `<img src="${card.dataset.pImg}" alt="" loading="lazy" width="44" height="54">` : '';
          a.innerHTML = `${img}<span><b></b></span><em></em>`;
          const nm = card.dataset.pName || '';
          const m = nm.match(/^(.*?)(N°\s?\d+)(.*)$/);
          const b = a.querySelector('b');
          if (m) {
            const num = document.createElement('span');
            num.className = 'nbu__num'; num.textContent = m[2];
            b.append(document.createTextNode(m[1]), num, document.createTextNode(m[3]));
          } else b.textContent = nm;
          a.querySelector('span').append(document.createTextNode(card.dataset.pPrice || ''));
          a.querySelector('em').textContent = cta;
          it.appendChild(a);
        }
        v.addEventListener('click', () => {
          if (v.paused) { tryPlay(v); it.classList.remove('is-paused'); } else { v.pause(); it.classList.add('is-paused'); }
        });
        list.appendChild(it);
        items.push({ el: it, v, card });
      });
      const rio = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (open && e.isIntersecting && e.intersectionRatio >= 0.6) activate(Number(e.target.dataset.i)); });
      }, { root: list, threshold: [0.6] });
      items.forEach((x) => rio.observe(x.el));
    };

    const openAt = (i) => {
      open = true;
      hooks.onOpen && hooks.onOpen();
      build();
      muted = false; syncMute();
      items.forEach((x) => { x.v.muted = false; });
      dlg.showModal();
      list.scrollTop = i * list.clientHeight;
      active = -1;
      activate(i); // sincron în gestul utilizatorului → sunetul e permis
    };

    const close = () => { if (dlg.open) dlg.close(); };

    dlg.addEventListener('close', () => {
      items.forEach((x) => release(x.v));
      open = false; active = -1;
      hooks.onClose && hooks.onClose();
    });
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-nbu-close]')) { close(); return; }
      if (e.target.closest('[data-nbu-mute]')) {
        muted = !muted; items.forEach((x) => { x.v.muted = muted; }); syncMute();
        if (!muted && items[active]) tryPlay(items[active].v);
        return;
      }
      const go = (d) => list.scrollBy({ top: d * list.clientHeight, behavior: reduced ? 'auto' : 'smooth' });
      if (e.target.closest('[data-nbu-up]')) { go(-1); return; }
      if (e.target.closest('[data-nbu-down]')) {
        if (active >= items.length - 1) list.scrollTo({ top: 0 }); else go(1);
      }
    });
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        list.scrollBy({ top: (e.key === 'ArrowDown' ? 1 : -1) * list.clientHeight, behavior: reduced ? 'auto' : 'smooth' });
      }
    });
    return { open: openAt, isOpen: () => open };
  };

  /* ---------- așezarea „strip” (rând de clipuri): rulează doar primul clip vizibil ---------- */
  const initStrip = (root) => {
    const hlsSrc = root.dataset.hlsSrc;
    const track = root.querySelector('[data-nbu-track]');
    const cards = [...root.querySelectorAll('[data-nbu-card]')];
    if (!cards.length) return;
    const autoplay = root.dataset.autoplay === 'true' && !reduced;
    const visible = new Set();
    let cur = null;

    const reels = wireReels(root, cards, hlsSrc, {
      onOpen: () => { releaseCard(cur); cur = null; },
      onClose: () => sync()
    });

    function sync() {
      if (!autoplay || reels.isOpen() || document.hidden) { if (cur && cur._v) cur._v.pause(); return; }
      const want = cards.find((c) => visible.has(c)) || null;
      if (want !== cur) { releaseCard(cur); cur = want; }
      if (!cur) return;
      const v = videoOf(cur);
      attachInline(v, cur, hlsSrc).then(() => { if (cur && cur._v === v && visible.has(cur)) tryPlay(v); });
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) visible.add(e.target); else visible.delete(e.target);
      });
      sync();
    }, { threshold: [0, 0.6] });
    cards.forEach((c) => io.observe(c));
    document.addEventListener('visibilitychange', sync);

    const prev = root.querySelector('[data-nbu-prev]');
    const nextBtn = root.querySelector('[data-nbu-next]');
    const step = () => Math.max(track.clientWidth * 0.8, 200);
    const syncArrows = () => {
      if (!prev || !nextBtn) return;
      prev.disabled = track.scrollLeft <= 4;
      nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' }));
    nextBtn && nextBtn.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', syncArrows, { passive: true });
    syncArrows();

    root.addEventListener('click', (e) => {
      const card = e.target.closest('[data-nbu-card]');
      if (card && root.contains(card)) reels.open(cards.indexOf(card));
    });
  };

  /* ---------- așezarea „smart” (GT): un singur clip activ, avansează singur, bare de progres ---------- */
  const initSmart = (root) => {
    const hlsSrc = root.dataset.hlsSrc;
    const track = root.querySelector('[data-nbu-track]');
    const slides = [...root.querySelectorAll('.nbu-s__it')];
    const cards = slides.map((s) => s.querySelector('[data-nbu-card]'));
    const segs = [...root.querySelectorAll('.nbu-s__segs i')];
    const pauseBtn = root.querySelector('[data-nbu-pause]');
    if (!slides.length) return;
    const autoplay = root.dataset.autoplay === 'true' && !reduced;
    const ms = Math.max(2000, Number(root.dataset.advance) || 4000);
    let cur = -1;
    let inView = false;
    let userPaused = false;
    let timer = null;
    let remaining = ms;
    let t0 = 0;
    let waitTimer = null;
    let scrollT = null;

    const reels = wireReels(root, cards, hlsSrc, {
      onOpen: () => { hold(); releaseCard(cards[cur]); },
      onClose: () => { resumeIfCan(); }
    });

    /* PERF (2 oct): redarea automată (hls.js 99 KB + ~0,9 MB/10 s) pornește abia după `load` + un moment liber, nu în
       fereastra în care se încarcă LCP-ul paginii (pe /pages/recenzii caruselul e în primul ecran). Atingerea o pornește imediat. */
    let pageReady = document.readyState === 'complete' && performance.now() > 4000;
    const markReady = () => { if (pageReady) return; pageReady = true; resumeIfCan(); };
    if (!pageReady) {
      const later = () => (window.requestIdleCallback || ((f) => setTimeout(f, 600)))(markReady, { timeout: 1500 });
      if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
      root.addEventListener('pointerdown', markReady, { once: true, passive: true });
    }
    const running = () => pageReady && autoplay && inView && !userPaused && !reels.isOpen() && !document.hidden;

    /* Pauză a ceasului de avansare + a barei de progres (păstrează timpul rămas). */
    function hold() {
      if (timer) { clearTimeout(timer); timer = null; remaining = Math.max(0, remaining - (performance.now() - t0)); }
      clearTimeout(waitTimer); waitTimer = null;
      root.classList.add('is-hold');
    }
    const startClock = () => {
      if (!running() || timer || slides.length < 2) return;
      root.classList.remove('is-hold');
      t0 = performance.now();
      timer = setTimeout(() => { timer = null; go(cur + 1, true); }, remaining);
    };

    const segReset = () => {
      segs.forEach((s, k) => {
        s.classList.remove('is-on', 'is-done');
        if (k < cur) s.classList.add('is-done');
      });
      const s = segs[cur];
      if (s) { void s.offsetWidth; s.classList.add('is-on'); } // reflow → animația pornește de la 0
    };

    function onPlay(c) { if (c === cards[cur]) startClock(); }

    /* Pornește clipul central; ceasul pornește la „playing” (sau după 5 s, ca să nu rămână blocat pe o rețea lentă). */
    const playCur = () => {
      const card = cards[cur];
      if (!card || !autoplay) return;
      const v = videoOf(card, onPlay);
      attachInline(v, card, hlsSrc).then(() => { if (card === cards[cur] && running()) tryPlay(v); });
      clearTimeout(waitTimer);
      waitTimer = setTimeout(() => { if (card === cards[cur]) startClock(); }, 5000);
    };

    const center = (i, smooth) => {
      const sl = slides[i];
      track.scrollTo({ left: sl.offsetLeft - (track.clientWidth - sl.offsetWidth) / 2, behavior: smooth && !reduced ? 'smooth' : 'auto' });
    };

    function go(n, scroll) {
      const i = (n + slides.length) % slides.length;
      if (i === cur) return;
      releaseCard(cards[cur]);
      hold();
      cur = i;
      remaining = ms;
      slides.forEach((s, k) => {
        s.classList.toggle('is-on', k === i);
        const a = s.querySelector('.nbu__shop');
        if (a) a.tabIndex = k === i ? 0 : -1;
      });
      segReset();
      if (scroll) center(i, true);
      if (running()) playCur();
    }

    function resumeIfCan() {
      if (!running()) return;
      const v = cards[cur] && cards[cur]._v;
      if (v && v._nbu) { tryPlay(v); if (!v.paused && v.readyState > 2) startClock(); }
      else playCur();
    }

    /* vizibilitate: nimic nu se descarcă până caruselul nu intră în ecran; la ieșire, bufferul se eliberează */
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        inView = e.isIntersecting && e.intersectionRatio >= 0.35;
        if (inView) resumeIfCan();
        else { hold(); releaseCard(cards[cur]); }
      });
    }, { threshold: [0, 0.35] });
    io.observe(track);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { hold(); const v = cards[cur] && cards[cur]._v; v && v.pause(); }
      else resumeIfCan();
    });

    /* glisare manuală: clipul cel mai aproape de centru devine activ */
    const nearest = () => {
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0; let bd = Infinity;
      slides.forEach((s, k) => { const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - mid); if (d < bd) { bd = d; best = k; } });
      return best;
    };
    track.addEventListener('scroll', () => {
      clearTimeout(scrollT);
      scrollT = setTimeout(() => { const k = nearest(); if (k !== cur) go(k, false); }, 140);
    }, { passive: true });

    root.addEventListener('click', (e) => {
      const card = e.target.closest('[data-nbu-card]');
      if (!card || !root.contains(card)) return;
      const k = cards.indexOf(card);
      if (k === cur) reels.open(k);
      else go(k, true);
    });

    const prev = root.querySelector('[data-nbu-prev]');
    const nextBtn = root.querySelector('[data-nbu-next]');
    prev && prev.addEventListener('click', () => go(cur - 1, true));
    nextBtn && nextBtn.addEventListener('click', () => go(cur + 1, true));

    const syncPause = () => {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('aria-pressed', String(userPaused));
      pauseBtn.querySelector('[data-on]').hidden = userPaused;
      pauseBtn.querySelector('[data-off]').hidden = !userPaused;
    };
    if (pauseBtn) {
      if (!autoplay) pauseBtn.hidden = true;
      pauseBtn.addEventListener('click', () => {
        userPaused = !userPaused; syncPause();
        const v = cards[cur] && cards[cur]._v;
        if (userPaused) { hold(); v && v.pause(); } else resumeIfCan();
      });
    }

    if (!autoplay) root.classList.add('is-static');
    root.classList.add('is-hold');
    go(0, false);
  };

  /* PERF (2 oct): posterele de la al 4-lea clip vin fără src (nubra-ugc-clip) → le punem când placa e la ≤ ½ lățime de zona
     vizibilă a benzii (root = banda; pe verticală decide tot `loading=lazy`). */
  const showPoster = (im) => {
    if (!im.hasAttribute('data-src')) return;
    im.srcset = im.getAttribute('data-srcset') || ''; im.src = im.getAttribute('data-src');
    im.removeAttribute('data-srcset'); im.removeAttribute('data-src');
  };
  const deferPosters = (root) => {
    const imgs = [...root.querySelectorAll('img[data-nbu-defer]')];
    if (!imgs.length) return;
    const track = root.querySelector('[data-nbu-track]');
    if (!track || !('IntersectionObserver' in window)) { imgs.forEach(showPoster); return; }
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { showPoster(e.target); io.unobserve(e.target); } });
    }, { root: track, rootMargin: '0px 50% 0px 50%' });
    imgs.forEach((im) => io.observe(im));
  };

  const init = (root) => {
    if (root.dataset.nbuWired) return;
    root.dataset.nbuWired = '1';
    deferPosters(root);
    if (root.dataset.layout === 'smart') initSmart(root);
    else initStrip(root);
  };

  const boot = () => document.querySelectorAll('[data-nbu]').forEach(init);
  window.NubraUgc = { boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
